import React, { useState } from 'react';
import { Building2, Zap, TrendingUp, Home, ExternalLink, Mail, Phone, MapPin, ArrowRight, Users, Globe } from 'lucide-react';
export function LandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="min-h-screen bg-zinc-950 text-white" data-id="element-64">
      {/* Hero */}
      <section className="pt-32 pb-24 px-6" data-id="element-65">
        <div className="max-w-6xl mx-auto text-center" data-id="element-66">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 text-amber-400 text-sm font-medium mb-8" data-id="element-67">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" data-id="element-68"></span>
            Hellstrom Holdings
          </div>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6" data-id="element-69">
            Built for infrastructure.{' '}
            <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent" data-id="element-70">
              Built for business.
            </span>
          </h1>
          <p className="text-zinc-400 text-xl max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-71">
            A holding company operating across real estate, technology, and
            consulting. We build ventures designed to last.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-72">
            <a href="#work" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-8 py-4 rounded-xl transition-colors" data-id="element-73">
              Our Work <ArrowRight className="w-4 h-4" data-id="element-74" />
            </a>
            <a href="#contact" className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-semibold px-8 py-4 rounded-xl transition-colors" data-id="element-75">
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section id="services" className="py-24 px-6 bg-zinc-900/50" data-id="element-76">
        <div className="max-w-6xl mx-auto" data-id="element-77">
          <div className="text-center mb-16" data-id="element-78">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-79">What We Do</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto" data-id="element-80">
              We build and operate across multiple verticals, bringing the same
              rigor and craftsmanship to every venture.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-81">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-colors" data-id="element-82">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6" data-id="element-83">
                <Building2 className="w-6 h-6 text-amber-400" data-id="element-84" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-85">Real Estate</h3>
              <p className="text-zinc-400 leading-relaxed" data-id="element-86">
                Acquiring, developing, and managing residential and commercial
                properties with a long-term ownership mindset.
              </p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-colors" data-id="element-87">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6" data-id="element-88">
                <Zap className="w-6 h-6 text-amber-400" data-id="element-89" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-90">Technology</h3>
              <p className="text-zinc-400 leading-relaxed" data-id="element-91">
                Building software products and digital infrastructure that solve
                real problems and scale efficiently.
              </p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-colors" data-id="element-92">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6" data-id="element-93">
                <TrendingUp className="w-6 h-6 text-amber-400" data-id="element-94" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-95">Consulting</h3>
              <p className="text-zinc-400 leading-relaxed" data-id="element-96">
                Advising businesses on growth strategy, operations, and
                execution — from early-stage to established enterprises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Ecosystem */}
      <section id="work" className="py-24 px-6" data-id="element-97">
        <div className="max-w-6xl mx-auto" data-id="element-98">
          <div className="text-center mb-16" data-id="element-99">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-100">
              Our Ecosystem
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto" data-id="element-101">
              A portfolio of ventures built to last — each one a reflection of
              our commitment to quality and long-term value.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-102">
            <a href="https://revisithomes.com" target="_blank" rel="noopener noreferrer" className="group bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-300 block" data-id="element-103">
              <div className="flex items-start justify-between mb-4" data-id="element-104">
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center" data-id="element-105">
                  <Home className="w-6 h-6 text-amber-400" data-id="element-106" />
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-amber-400 transition-colors" data-id="element-107" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-108">Revisit Homes</h3>
              <p className="text-zinc-400 leading-relaxed text-sm" data-id="element-109">
                A real estate platform focused on thoughtful property
                acquisition and management.
              </p>
            </a>
            <a href="https://revisitx.com" target="_blank" rel="noopener noreferrer" className="group bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-300 block" data-id="element-110">
              <div className="flex items-start justify-between mb-4" data-id="element-111">
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center" data-id="element-112">
                  <Globe className="w-6 h-6 text-amber-400" data-id="element-113" />
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-amber-400 transition-colors" data-id="element-114" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-115">Revisit X</h3>
              <p className="text-zinc-400 leading-relaxed text-sm" data-id="element-116">
                Our technology arm, building digital products and infrastructure
                for modern businesses.
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Meet the Brothers */}
      <section id="about" className="py-24 px-6 bg-zinc-900/50" data-id="element-117">
        <div className="max-w-6xl mx-auto" data-id="element-118">
          <div className="text-center mb-16" data-id="element-119">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-120">
              Meet the Brothers
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto" data-id="element-121">
              Two brothers with complementary skills, a shared vision, and a
              relentless drive to build things that matter.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-122">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8" data-id="element-123">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-6" data-id="element-124">
                <Users className="w-8 h-8 text-amber-400" data-id="element-125" />
              </div>
              <h3 className="text-2xl font-bold mb-1" data-id="element-126">Elias Hellstrom</h3>
              <p className="text-amber-400 text-sm font-medium mb-4" data-id="element-127">
                Co-Founder
              </p>
              <p className="text-zinc-400 leading-relaxed" data-id="element-128">
                Elias brings deep expertise in real estate and business
                development, with a track record of identifying and executing on
                high-value opportunities.
              </p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8" data-id="element-129">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-6" data-id="element-130">
                <Users className="w-8 h-8 text-amber-400" data-id="element-131" />
              </div>
              <h3 className="text-2xl font-bold mb-1" data-id="element-132">Travis Hellstrom</h3>
              <p className="text-amber-400 text-sm font-medium mb-4" data-id="element-133">
                Co-Founder
              </p>
              <p className="text-zinc-400 leading-relaxed" data-id="element-134">
                Travis leads technology and strategy, combining a builder's
                mindset with operational discipline to scale ventures from
                concept to execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6" data-id="element-135">
        <div className="max-w-6xl mx-auto" data-id="element-136">
          <div className="text-center mb-16" data-id="element-137">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-138">
              Get in Touch
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto" data-id="element-139">
              Whether you're looking to partner, invest, or just learn more —
              we'd love to hear from you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto" data-id="element-140">
            <div data-id="element-141">
              <div className="space-y-6" data-id="element-142">
                <div className="flex items-center gap-4" data-id="element-143">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-144">
                    <Mail className="w-5 h-5 text-amber-400" data-id="element-145" />
                  </div>
                  <div data-id="element-146">
                    <p className="text-zinc-500 text-sm" data-id="element-147">Email</p>
                    <p className="text-white" data-id="element-148">hello@hellstromholdings.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-149">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-150">
                    <MapPin className="w-5 h-5 text-amber-400" data-id="element-151" />
                  </div>
                  <div data-id="element-152">
                    <p className="text-zinc-500 text-sm" data-id="element-153">Location</p>
                    <p className="text-white" data-id="element-154">United States</p>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-155">
              {submitted ? <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center" data-id="element-156">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-157">
                    <Mail className="w-6 h-6 text-amber-400" data-id="element-158" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" data-id="element-159">Message Sent</h3>
                  <p className="text-zinc-400" data-id="element-160">We'll be in touch shortly.</p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-161">
                  <div data-id="element-162">
                    <label className="block text-sm font-medium text-zinc-400 mb-2" data-id="element-163">
                      Name
                    </label>
                    <input type="text" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors" placeholder="Your name" required data-id="element-164" />
                  </div>
                  <div data-id="element-165">
                    <label className="block text-sm font-medium text-zinc-400 mb-2" data-id="element-166">
                      Email
                    </label>
                    <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors" placeholder="your@email.com" required data-id="element-167" />
                  </div>
                  <div data-id="element-168">
                    <label className="block text-sm font-medium text-zinc-400 mb-2" data-id="element-169">
                      Message
                    </label>
                    <textarea value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} rows={4} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors resize-none" placeholder="Tell us about your project or inquiry..." required data-id="element-170" />
                  </div>
                  <button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-8 py-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-171">
                    Send Message <ArrowRight className="w-4 h-4" data-id="element-172" />
                  </button>
                </form>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-6" data-id="element-173">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-174">
          <p className="text-zinc-500 text-sm" data-id="element-175">
            © 2024 Hellstrom Holdings. All rights reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-176">
            <a href="#services" className="text-zinc-500 hover:text-white text-sm transition-colors" data-id="element-177">
              Services
            </a>
            <a href="#work" className="text-zinc-500 hover:text-white text-sm transition-colors" data-id="element-178">
              Work
            </a>
            <a href="#about" className="text-zinc-500 hover:text-white text-sm transition-colors" data-id="element-179">
              About
            </a>
            <a href="#contact" className="text-zinc-500 hover:text-white text-sm transition-colors" data-id="element-180">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>;
}